import { createContext, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api, formatApiError, portalForPath } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = unauthenticated
  const [error, setError] = useState("");

  // Staff and patient sessions are separate now, so "who am I" has two answers in one
  // browser and the route picks which one is being asked. Resolve it again on crossing
  // between the portals, or the clinic portal would keep showing whoever was resolved
  // first — the same stale identity the split cookies exist to prevent.
  const portal = portalForPath(useLocation().pathname);

  useEffect(() => {
    let stale = false;
    setUser(null);                       // checking, for THIS portal
    api
      .get("/auth/me")
      .then((res) => { if (!stale) setUser(res.data); })
      .catch(() => { if (!stale) setUser(false); });
    return () => { stale = true; };
  }, [portal]);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data?.mfa_required) {
        return { mfaRequired: true };
      }
      setUser(data);
      return { ok: true };
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
      return { ok: false };
    }
  };

  const verifyMfa = async (code) => {
    setError("");
    try {
      const { data } = await api.post("/auth/mfa/verify", { code });
      setUser(data);
      return { ok: true };
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
      return { ok: false };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // ignore
    }
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, login, verifyMfa, logout, error, setError }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
